import gql from 'graphql-tag';
import { ChannelSubscriptionType } from '../shared/types';

export enum CreateTaxIdResult {
    NotSet = 'NotSet',
    MaxTaxIDError = 'MaxTaxIDError',
    TaxIdAlreadyExists = 'TaxIdAlreadyExists',
    TaxIdInvalidFormat = 'TaxIdInvalidFormat',
    UnknownError = 'UnknownError',
    Success = 'Success',
}

export interface StripePendingSubscription {
    createStripeSubscription: {
        subscriptionId: string;
        clientSecret: string;
        addressIsValid: boolean;
    };
}

export interface CreateStripeCustomerInput extends CreateStripeSubscriptionInput {
    taxIdType: string;
}

export interface CreateStripeSubscriptionInput {
    channelId: string;
    taxId: string;
    addressLine1: string;
    addressLine2: string;
    addressState: string;
    addressCity: string;
    addressPostcode: string;
    addressCountryCode: string;
}

export interface BulkChannelSubscriptionsInput {
    channelId: string;
    cancelAt?: string;
    accountNumber?: string;
    notes: string;
    subscriptionType: ChannelSubscriptionType;
    pricePerMonth?: number;
    rebate?: boolean;
    isFullArticleContent?: boolean;
}

export const SUBSCRIPTIONS = gql`
    query getSubscriptions {
        me {
            subscriptions {
                categories {
                    isEmail
                    category {
                        _id
                        name
                        parent
                    }
                }
            }
            channelSubscriptions {
                channel {
                    _id
                    name
                }
                subscribed
                emailNotifications
            }
        }
    }
`;

export const CHANGE_SUBSCRIPTION = gql`
    mutation changeSubscription($item: UserSubscriptionChange) {
        changeSubscription(item: $item)
    }
`;

export const CHANGE_EMAIL_PREFERENCE = gql`
    mutation changeEmailPreference($item: EmailPreferenceChange) {
        changeEmailPreference(item: $item) {
            enabled
            itemList
        }
    }
`;

export const SET_SUBSCRIPTIONS = gql`
    mutation setSubscriptions($item: UserAddSubscriptions) {
        setSubscriptions(item: $item)
    }
`;

export const CREATE_STRIPE_CUSTOMER = gql`
    mutation createStripeCustomer($item: CreateStripeCustomerInput!) {
        createStripeCustomer(item: $item)
    }
`;

export const CREATE_STRIPE_SUBSCRIPTION = gql`
    mutation createStripeSubscription($item: CreateStripeSubscriptionInput!) {
        createStripeSubscription(item: $item) {
            subscriptionId
            clientSecret
            addressIsValid
        }
    }
`;

export const CANCEL_CHANNEL_SUBSCRIPTION = gql`
    mutation cancelSubscription($channelId: ID!) {
        cancelSubscription(channelId: $channelId)
    }
`;

export const CREATE_NON_CHARGE_CHANNEL_SUBSCRIPTION = gql`
    mutation createNonChargeSubscription(
        $item: UpsertChannelSubscriptionInput!
    ) {
        createNonChargeChannelSubscription(item: $item) {
            subscriptionState
            subscriptionType
            notes
            isFullArticleContent
        }
    }
`;

export const UPDATE_NON_CHARGE_CHANNEL_SUBSCRIPTION = gql`
    mutation updateNonChargeSubscription(
        $item: UpsertChannelSubscriptionInput!
    ) {
        updateNonChargeChannelSubscription(item: $item) {
            cancelAt
            subscriptionState
            subscriptionType
            notes
            isFullArticleContent
        }
    }
`;

export const CREATE_EXTERNAL_BILLING_CHANNEL_SUBSCRIPTION = gql`
    mutation createExternalBillingChannelSubscription(
        $item: UpsertChannelSubscriptionInput!
    ) {
        createExternalBillingChannelSubscription(item: $item) {
            subscriptionState
            subscriptionType
            pricePerMonth
            billingMethod
            isFullArticleContent
        }
    }
`;

export const UPDATE_EXTERNAL_BILLING_CHANNEL_SUBSCRIPTION = gql`
    mutation updateExternalBillingChannelSubscription(
        $item: UpsertChannelSubscriptionInput!
    ) {
        updateExternalBillingChannelSubscription(item: $item) {
            subscriptionState
            subscriptionType
            pricePerMonth
            billingMethod
            isFullArticleContent
        }
    }
`;

export const NON_CHARGE_CHANNEL_SUBSCRIPTION_EXPIRED_ACKNOWLEDGED = gql`
    mutation nonChargeChannelSubscriptionExpiredAcknowledged($channelId: ID!) {
        nonChargeChannelSubscriptionExpiredAcknowledged(channelId: $channelId)
    }
`;

export const CANCEL_NON_STRIPE_SUBSCRIPTION_IMMEDIATELY = gql`
    mutation cancelNonStripeSubscriptionImmediately(
        $channelId: ID!
        $userId: String!
    ) {
        cancelNonStripeSubscriptionImmediately(
            channelId: $channelId
            userId: $userId
        )
    }
`;

export const CREATE_TRIAL_CHANNEL_SUBSCRIPTION = gql`
    mutation createTrialChannelSubscription(
        $item: UpsertChannelSubscriptionInput!
    ) {
        createTrialChannelSubscription(item: $item) {
            channelId
            notes
            isFullArticleContent
            trialDuration
        }
    }
`;

export const UPDATE_TRIAL_CHANNEL_SUBSCRIPTION = gql`
    mutation updateTrialChannelSubscription(
        $item: UpsertChannelSubscriptionInput!
    ) {
        updateTrialChannelSubscription(item: $item) {
            channelId
            notes
            isFullArticleContent
            trialDuration
        }
    }
`;

export const REQUEST_TRIAL_CHANNEL_SUBSCRIPTION = gql`
    mutation requestTrialChannelSubscription(
        $item: NotifyRegisterChannelInterestInput!
    ) {
        requestTrialChannelSubscription(item: $item) {
            channelName
            userEmail
        }
    }
`;

export const CREATE_ACCOUNT_CHANNEL_SUBSCRIPTION = gql`
    mutation createAccountChannelSubscription(
        $item: UpsertChannelSubscriptionInput!
    ) {
        createAccountChannelSubscription(item: $item) {
            channelId
            cancelAt
            isFullArticleContent
        }
    }
`;

export const CREATE_OPEN_FREE_CHANNEL_SUBSCRIPTION = gql`
    mutation createOpenFreeChannelSubscription(
        $item: CreateChannelSubscriptionInput!
    ) {
        createOpenFreeChannelSubscription(item: $item) {
            channelId
        }
    }
`;

export const REQUEST_FREE_CLOSED_CHANNEL_SUBSCRIPTION = gql`
    mutation requestFreeClosedSubscription($channelId: ID!) {
        requestFreeClosedSubscription(channelId: $channelId) {
            channelId
        }
    }
`;

export const UPDATE_ACCOUNT_CHANNEL_SUBSCRIPTION = gql`
    mutation updateAccountChannelSubscription(
        $item: UpsertChannelSubscriptionInput!
    ) {
        updateAccountChannelSubscription(item: $item) {
            channelId
            cancelAt
            isFullArticleContent
        }
    }
`;

export const CREATE_FREE_CHANNEL_SUBSCRIPTION = gql`
    mutation createFreeChannelSubscription(
        $item: UpsertChannelSubscriptionInput!
    ) {
        createFreeChannelSubscription(item: $item) {
            channelId
            cancelAt
            isFullArticleContent
        }
    }
`;

export const UPDATE_FREE_CHANNEL_SUBSCRIPTION = gql`
    mutation updateAccountChannelSubscription(
        $item: UpsertChannelSubscriptionInput!
    ) {
        updateFreeChannelSubscription(item: $item) {
            channelId
            cancelAt
            isFullArticleContent
        }
    }
`;

export const CREATE_INVOICE_PAYMENT_REQUEST = gql`
    mutation createInvoicePaymentRequest($item: InvoicePaymentRequestInput!) {
        createInvoicePaymentRequest(item: $item)
    }
`;

export const CREATE_BULK_CHANNEL_SUBSCRIPTIONS = gql`
    mutation createBulkSubscriptions(
        $item: BulkChannelSubscriptionInput
        $users: [String]
        $existing: [String]
    ) {
        createBulkSubscriptions(item: $item, users: $users, existing: $existing)
    }
`;
